import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Label, Row } from "reactstrap";
import { AsyncPaginate } from "react-select-async-paginate";
import ValidationMessage from "src/Components/Form/ValidationMessage";
import __ from "src/utils/Translations";
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from "src/utils/constants/api";
import { defaultStyles, errorStyles } from "./Autocomplete";

const AsyncAutocompletePaginated = ({
  value,
  label,
  onChange,
  id,
  errorMessage,
  validateField,
  validation,
  fetchOptions,
  tooltip,
  inputSwitcher,
  disabled,
  isMultiselect,
  placeholder,
  classNamePrefix,
  customItemsCountPerRequest,
  shouldFetchOnFirstEntry,
  loadOnOpen,
}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const timer = useRef();

  useEffect(() => {
    if (!value && selectedValue) {
      setSelectedValue(null);
    }
  }, [value, selectedValue, setSelectedValue]);

  useEffect(() => {
    const fetchData = async () => {
      if (value && shouldFetchOnFirstEntry) {
        const res = await fetchOptions();
        setSelectedValue(res);
        return;
      }
      if (value) setSelectedValue(value);
    };

    fetchData();
  }, [value, shouldFetchOnFirstEntry]);

  const loadOptions = useCallback(
    async (inputValue, loadedOptions, { page }) => {
      if (timer.current) clearTimeout(timer.current);

      return new Promise((resolve) => {
        timer.current = setTimeout(async () => {
          if (
            (inputValue && inputValue.length > 1) ||
            (!inputValue && value?.length) ||
            loadOnOpen
          ) {
            const newOptions = await fetchOptions(inputValue, page);

            resolve({
              options: newOptions,
              hasMore:
                newOptions.length >=
                (customItemsCountPerRequest || DEFAULT_ITEMS_COUNT_PER_REQUEST), // Assuming more data is available if new options were loaded
              additional: {
                page: page + 1,
              },
            });
          } else {
            resolve({
              options: [],
              hasMore: false,
              additional: {
                page: 1,
              },
            });
          }
        }, 500);
      });
    },
    [timer, loadOnOpen, customItemsCountPerRequest, fetchOptions]
  );

  const inputGroup = (
    <div
      className="input-group-omb font-weight-normal text-decoration-none"
      data-t1={id}
    >
      <AsyncPaginate
        key={`${id}_autocomplete-${value}`}
        isClearable
        className="omb-autocomplete font-weight-normal text-decoration-none"
        isDisabled={disabled}
        isMulti={isMultiselect}
        noOptionsMessage={() => __("Brak opcji")}
        loadingMessage={() => __("Wczytywanie...")}
        hideSelectedOptions={false}
        classNamePrefix={classNamePrefix}
        menuPlacement="auto"
        styles={errorMessage ? errorStyles : defaultStyles}
        value={selectedValue}
        onChange={(option, triggeredAction) => {
          const value = option?.length ? option[0]?.value : option?.value;
          const isRemoveAction = triggeredAction.action.includes("remove");
          const isClearAction = triggeredAction.action.includes("clear");
          setSelectedValue(option);
          onChange(
            isClearAction ? [] : option,
            isRemoveAction || isClearAction
          );
          validateField(id, value, validation);
        }}
        placeholder={placeholder || __("Zacznij wpisywać...")}
        onKeyDown={(e) => e.stopPropagation()}
        onBlur={async () => {
          if (validateField) {
            validateField(id, value, validation);
          }
        }}
        loadOptions={loadOptions}
        additional={{
          page: 1, // Start pagination at page 1
        }}
        inputValue={searchValue}
        onInputChange={(query) => setSearchValue(query)}
      />
      <ValidationMessage message={errorMessage} />
    </div>
  );

  return (
    <>
      {label ? (
        <Label data-t1={`${id}Label`} for={id} className="mr-sm-2">
          <span key={`label-${label}`}>{label}</span>
          {tooltip ? <span key="tooltip"> {tooltip}</span> : null}
        </Label>
      ) : null}
      {inputSwitcher ? (
        <Row>
          <Col sm={9}>{inputGroup}</Col>
          <Col sm={3}>{inputSwitcher}</Col>
        </Row>
      ) : (
        inputGroup
      )}
    </>
  );
};

AsyncAutocompletePaginated.propTypes = {
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]),
  tooltip: PropTypes.node,
  errorMessage: PropTypes.string,
  classNamePrefix: PropTypes.string,
  placeholder: PropTypes.string,
  validateField: PropTypes.func,
  inputSwitcher: PropTypes.node,
  disabled: PropTypes.bool,
  isMultiselect: PropTypes.bool,
  customItemsCountPerRequest: PropTypes.number,
  validation: PropTypes.array,
  fetchOptions: PropTypes.func.isRequired,
  shouldFetchOnFirstEntry: PropTypes.bool,
  loadOnOpen: PropTypes.bool,
};

AsyncAutocompletePaginated.defaultProps = {
  id: "",
  label: "",
  value: "",
  errorMessage: "",
  inputSwitcher: null,
  classNamePrefix: null,
  disabled: false,
  isMultiselect: false,
  customItemsCountPerRequest: DEFAULT_ITEMS_COUNT_PER_REQUEST,
  tooltip: null,
  placeholder: null,
  validateField: () => {},
  validation: [],
  shouldFetchOnFirstEntry: false,
  loadOnOpen: true,
};

export default AsyncAutocompletePaginated;
